/**
 * ADV Collection Landing — JS
 * File: assets/adv-collection-landing.js
 *
 * Responsibilities:
 *  1. Carousel scroll + prev/next arrow state
 *  2. FAQ accordion open/close animation (details/summary)
 *  3. Quick-add toast notification (listens to theme's ajax-cart events)
 *
 * No dependencies. Vanilla JS only.
 * Loaded deferred via the template JSON.
 */

(function () {
  'use strict';

  /* ─── 1. Carousel controller ──────────────────────────────── */

  /**
   * Wire up arrow buttons for a given carousel element.
   * Buttons carry data-carousel="<id>" and data-dir="-1|1".
   */
  function initCarousel(carouselEl) {
    if (!carouselEl) return;

    const id = carouselEl.id;
    const prevBtn = document.querySelector(
      `.adv-carousel__arrow--prev[data-carousel="${id}"]`
    );
    const nextBtn = document.querySelector(
      `.adv-carousel__arrow--next[data-carousel="${id}"]`
    );

    if (!prevBtn && !nextBtn) return;

    function updateArrows() {
      const atStart = carouselEl.scrollLeft <= 4;
      const atEnd =
        carouselEl.scrollLeft + carouselEl.clientWidth >=
        carouselEl.scrollWidth - 4;
      if (prevBtn) prevBtn.disabled = atStart;
      if (nextBtn) nextBtn.disabled = atEnd;
    }

    function scrollBy(dir) {
      // Page by one card width + gap
      const card = carouselEl.querySelector('[data-bag-card], .adv-carousel__item, .adv-review-card');
      const gap = 24; // matches CSS gap (1.5rem ≈ 24px)
      const step = card
        ? card.offsetWidth + gap
        : carouselEl.clientWidth * 0.8;
      carouselEl.scrollBy({ left: dir * step, behavior: 'smooth' });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => scrollBy(-1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => scrollBy(1));
    }

    carouselEl.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows, { passive: true });

    // Initial state
    updateArrows();
  }

  function initAllCarousels() {
    document.querySelectorAll('.adv-carousel, .adv-reviews-carousel').forEach(initCarousel);
  }

  /* ─── 2. FAQ accordion — animate height ──────────────────── */
  /**
   * Browsers animate <details> poorly by default.
   * We use a simple CSS transition on max-height instead.
   */
  function initAccordions() {
    document.querySelectorAll('.adv-accordion').forEach(function (details) {
      const summary = details.querySelector('.adv-accordion__trigger');
      if (!summary) return;

      summary.addEventListener('click', function (e) {
        // Let the default toggle happen, then handle animation next frame
        if (details.open) {
          // Currently open → close it
          e.preventDefault();
          details.classList.add('adv-accordion--closing');
          const body = details.querySelector('.adv-accordion__body');
          if (body) {
            body.style.maxHeight = body.scrollHeight + 'px';
            requestAnimationFrame(() => {
              body.style.maxHeight = '0';
            });
            body.addEventListener(
              'transitionend',
              function handler() {
                details.open = false;
                details.classList.remove('adv-accordion--closing');
                body.style.maxHeight = '';
                body.removeEventListener('transitionend', handler);
              },
              { once: true }
            );
          } else {
            details.open = false;
          }
        }
        // Opening: browser handles natively; we just set max-height
      });

      details.addEventListener('toggle', function () {
        if (details.open) {
          const body = details.querySelector('.adv-accordion__body');
          if (body) {
            body.style.maxHeight = '0';
            requestAnimationFrame(() => {
              body.style.maxHeight = body.scrollHeight + 'px';
              body.addEventListener(
                'transitionend',
                function handler() {
                  body.style.maxHeight = ''; // let CSS handle it
                  body.removeEventListener('transitionend', handler);
                },
                { once: true }
              );
            });
          }
        }
      });
    });
  }

  // Add transition CSS for accordion body programmatically
  (function injectAccordionCSS() {
    const style = document.createElement('style');
    style.textContent = `
      .adv-accordion__body {
        overflow: hidden;
        transition: max-height 280ms ease;
      }
    `;
    document.head.appendChild(style);
  })();

  /* ─── 3. Quick-add toast ─────────────────────────────────── */
  /**
   * The Split theme fires a custom event after AJAX cart add.
   * We listen for it and show a lightweight toast.
   *
   * Split theme events to try (most common):
   *   'cart:item-added'   — fired on document by product-form web component
   *   'theme:cart:added'  — alternate event name in some Split versions
   */
  const TOAST_EVENTS = ['cart:item-added', 'theme:cart:added', 'product:added-to-cart'];

  let toastTimer = null;

  function showToast(message) {
    let toast = document.getElementById('adv-quickadd-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'adv-quickadd-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.style.cssText = [
        'position:fixed',
        'bottom:1.5rem',
        'left:50%',
        'transform:translateX(-50%)',
        'z-index:9999',
        'background:#0a0a0a',
        'color:#f0ede6',
        'padding:0.75rem 1.25rem',
        'font-family:"Montserrat","Helvetica Neue",sans-serif',
        'font-weight:700',
        'font-size:0.75rem',
        'letter-spacing:0.08em',
        'text-transform:uppercase',
        'border-radius:2px',
        'box-shadow:0 8px 32px rgba(0,0,0,0.24)',
        'pointer-events:none',
        'opacity:0',
        'transition:opacity 200ms ease',
        'white-space:nowrap',
      ].join(';');
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    // Force reflow so transition fires
    toast.getBoundingClientRect();
    toast.style.opacity = '1';

    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.style.opacity = '0';
    }, 1800);
  }

  TOAST_EVENTS.forEach(function (eventName) {
    document.addEventListener(eventName, function (e) {
      const productTitle =
        e.detail?.product?.title ||
        e.detail?.title ||
        e.detail?.item?.title ||
        'Item';
      showToast(productTitle + ' added');
    });
  });

  /* ─── 4. Swatch color mapping ────────────────────────────── */
  /**
   * Swatches use CSS variables like --adv-swatch-midnight.
   * Inject a palette based on ADV's known color names.
   * Extend this map to match your actual variant option values.
   *
   * If you use a metafield-based color map (recommended for scale),
   * replace this with a fetch from your color_swatches.json asset.
   */
  const ADV_SWATCH_MAP = {
    midnight:  '#1a1a1a',
    dune:      '#e8dcc4',
    volt:      '#c4f04c',
    lava:      '#d96552',
    aqua:      '#7dd3c7',
    white:     '#f5f3ee',
    black:     '#0a0a0a',
    grey:      '#8a8a85',
    gray:      '#8a8a85',
    blue:      '#4a7cc5',
    navy:      '#1a2a4a',
    red:       '#d64040',
    green:     '#3d9e6e',
    pink:      '#e87b9a',
    purple:    '#9b72cf',
    blackvolt:  'linear-gradient(135deg, #0a0a0a 0 50%, #c4f04c 50% 100%)',
    blacklava:  'linear-gradient(135deg, #0a0a0a 0 50%, #d96552 50% 100%)',
    blackwhite: 'linear-gradient(135deg, #0a0a0a 0 50%, #f5f3ee 50% 100%)',
    whitevolt:  'linear-gradient(135deg, #f5f3ee 0 50%, #c4f04c 50% 100%)',
    whitelava:  'linear-gradient(135deg, #f5f3ee 0 50%, #d96552 50% 100%)',
    whiteblack: 'linear-gradient(135deg, #f5f3ee 0 50%, #0a0a0a 50% 100%)',
    crystal: '#ffffff',
    marble: '#8A8A8A',
    marine: '#0066C3',
    yellow: '#FFFF00',
    coral: '#F15C65',
  };

  (function injectSwatchCSS() {
    const vars = Object.entries(ADV_SWATCH_MAP)
      .map(([name, hex]) => `--adv-swatch-${name}: ${hex};`)
      .join('\n    ');
    const style = document.createElement('style');
    style.textContent = `:root {\n    ${vars}\n  }`;
    document.head.appendChild(style);
  })();

  /* ─── Init ───────────────────────────────────────────────── */
  function init() {
    initAllCarousels();
    initAccordions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
